import { useQuery } from "@tanstack/react-query";
import { gql, request } from "graphql-request";
import { useAccount, useReadContracts } from "wagmi";
import { reservoirAbi } from "@/abis/reservoir";
import {
  ENVIO_GRAPHQL_ENDPOINT_PRIMARY,
  ENVIO_GRAPHQL_ENDPOINT_FALLBACK,
} from "@/constants/api";
import { RESERVOIR_ADDRESSES } from "@/constants/contracts";
import { STRIKE_PRICES } from "@/constants/strikePrices";

// Berachain mainnet chain ID
const BERACHAIN_CHAIN_ID = 80094;

// Map strike prices to reservoir indices
const STRIKE_TO_INDEX: Record<string, number> = STRIKE_PRICES.reduce(
  (acc, strike, index) => {
    acc[strike.toString()] = index;
    return acc;
  },
  {} as Record<string, number>,
);

// Envio/Hasura GraphQL query for vault data
const VAULT_DATA_QUERY = gql`
  query GetVaultData($userAddress: String!, $chainId: Int!) {
    HenloVaultRound(
      where: { chainId: { _eq: $chainId } }
      order_by: { strike: asc }
    ) {
      id
      strike
      epochId
      exists
      closed
      depositsPaused
      depositLimit
      totalDeposits
      whaleDeposits
      userDeposits
      remainingCapacity
      canRedeem
    }
    HenloVaultBalance(
      where: { user: { _eq: $userAddress }, chainId: { _eq: $chainId } }
    ) {
      strike
      balance
    }
    HenloVaultDeposit(
      where: { user: { _eq: $userAddress }, chainId: { _eq: $chainId } }
    ) {
      strike
      amount
    }
    HenloVaultStats(where: { chainId: { _eq: $chainId } }) {
      totalDeposits
      totalUsers
    }
    HenloVaultEpoch(
      where: { chainId: { _eq: $chainId } }
      order_by: { strike: asc }
    ) {
      id
      strike
      closed
      depositsPaused
      totalWhitelistDeposit
      totalMatched
    }
  }
`;

export interface VaultRound {
  id: string;
  strike: string;
  epochId: string;
  exists: boolean;
  closed: boolean;
  depositsPaused: boolean;
  depositLimit: string;
  totalDeposits: string;
  whaleDeposits: string;
  userDeposits: string;
  remainingCapacity: string;
  canRedeem: boolean;
}

export interface VaultBalance {
  strike: string;
  balance: string;
}

export interface VaultDeposit {
  strike: string;
  amount: string;
}

export interface VaultEpoch {
  id: string;
  strike: string;
  closed: boolean;
  depositsPaused: boolean;
  totalWhitelistDeposit: string;
  totalMatched: string;
}

// Envio response structure (Hasura PascalCase entity names)
interface EnvioVaultData {
  HenloVaultRound: VaultRound[];
  HenloVaultBalance: VaultBalance[];
  HenloVaultDeposit: VaultDeposit[];
  HenloVaultStats: {
    totalDeposits: string;
    totalUsers: number;
  }[];
  HenloVaultEpoch: VaultEpoch[];
}

export function useVaultData() {
  const { address } = useAccount();

  const { data, isLoading, error } = useQuery({
    queryKey: ["vault-data", address?.toLowerCase() || "", BERACHAIN_CHAIN_ID],
    queryFn: async () => {
      const variables = {
        userAddress: address?.toLowerCase() || "",
        chainId: BERACHAIN_CHAIN_ID,
      };

      // Try primary endpoint first, fallback to secondary if it fails
      try {
        return await request<EnvioVaultData>(
          ENVIO_GRAPHQL_ENDPOINT_PRIMARY,
          VAULT_DATA_QUERY,
          variables,
        );
      } catch {
        // Primary failed, try fallback
        return await request<EnvioVaultData>(
          ENVIO_GRAPHQL_ENDPOINT_FALLBACK,
          VAULT_DATA_QUERY,
          variables,
        );
      }
    },
    refetchInterval: 3000,
  });

  // Map Envio response to local variable names for easier access
  const vaultRounds = data?.HenloVaultRound || [];
  const vaultBalances = data?.HenloVaultBalance || [];
  const vaultDeposits = data?.HenloVaultDeposit || [];
  const vaultStats = data?.HenloVaultStats || [];
  const vaultEpochs = data?.HenloVaultEpoch || [];

  // Read totalWhitelistDeposit from all reservoirs
  const reservoirReads = Object.entries(RESERVOIR_ADDRESSES).map(([_index, address]) => {
    return {
      address: address as `0x${string}`,
      abi: reservoirAbi,
      functionName: "totalWhitelistDeposit" as const,
    };
  });

  const { data: reservoirData } = useReadContracts({
    contracts: reservoirReads,
  });

  // Create a map of strike to reservoir totalWhitelistDeposit
  const reservoirDepositsMap = Object.entries(RESERVOIR_ADDRESSES).reduce(
    (acc, [index, _], arrayIndex) => {
      const result = reservoirData?.[arrayIndex]?.result;
      acc[index] = typeof result === "bigint" ? result : 0n;
      return acc;
    },
    {} as Record<string, bigint>,
  );

  // Pre-calculate personal deposits per strike from the separate deposits query
  const personalDepositsMap = vaultDeposits.reduce(
    (acc, deposit) => {
      const strike = deposit.strike;
      acc[strike] = (acc[strike] || 0n) + BigInt(deposit.amount);
      return acc;
    },
    {} as Record<string, bigint>,
  );

  const availableStrikes =
    vaultRounds
      .filter(
        (round) =>
          round.exists &&
          !round.closed &&
          !round.depositsPaused &&
          BigInt(round.remainingCapacity) > 0n,
      )
      .map((round) => {
        // Find matching epoch data for this strike
        const epochData = vaultEpochs.find((epoch) => epoch.strike === round.strike);

        // Get the reservoir index for this strike
        const reservoirIndex = STRIKE_TO_INDEX[round.strike];

        // If reservoirIndex is undefined, try to find the closest match or use a default
        const effectiveReservoirIndex =
          reservoirIndex !== undefined
            ? reservoirIndex
            : STRIKE_PRICES.findIndex((strike) => strike.toString() === round.strike);

        // Get totalWhitelistDeposit from epoch or reservoir if epoch shows 0
        const totalWhitelistDeposit =
          epochData && BigInt(epochData.totalWhitelistDeposit) > 0n
            ? BigInt(epochData.totalWhitelistDeposit)
            : (effectiveReservoirIndex !== -1
                ? reservoirDepositsMap[effectiveReservoirIndex]
                : 0n) || 0n;

        const whaleDeposits = BigInt(round.whaleDeposits);
        const remainingMatchable =
          totalWhitelistDeposit > whaleDeposits ? totalWhitelistDeposit - whaleDeposits : 0n;

        return {
          strike: Number(round.strike),
          remainingCapacity: BigInt(round.remainingCapacity),
          depositLimit: BigInt(round.depositLimit),
          totalDeposits: BigInt(round.totalDeposits),
          whaleDeposits: whaleDeposits,
          userDeposits: BigInt(round.userDeposits),
          canRedeem: round.canRedeem,
          remainingMatchable,
          totalWhitelistDeposit,
        };
      }) || [];

  return {
    rounds: vaultRounds.map((round) => {
      // Find matching epoch data for this strike
      const epochData = vaultEpochs.find((epoch) => epoch.strike === round.strike);

      // Get the reservoir index for this strike
      const reservoirIndex = STRIKE_TO_INDEX[round.strike];

      // If reservoirIndex is undefined, try to find the closest match or use a default
      const effectiveReservoirIndex =
        reservoirIndex !== undefined
          ? reservoirIndex
          : STRIKE_PRICES.findIndex((strike) => strike.toString() === round.strike);

      // Get totalWhitelistDeposit from epoch or reservoir if epoch shows 0
      const totalWhitelistDeposit =
        epochData && BigInt(epochData.totalWhitelistDeposit) > 0n
          ? BigInt(epochData.totalWhitelistDeposit)
          : (effectiveReservoirIndex !== -1
              ? reservoirDepositsMap[effectiveReservoirIndex]
              : 0n) || 0n;

      const whaleDeposits = BigInt(round.whaleDeposits);
      const remainingMatchable =
        totalWhitelistDeposit > whaleDeposits ? totalWhitelistDeposit - whaleDeposits : 0n;

      return {
        ...round,
        strike: BigInt(round.strike),
        depositLimit: BigInt(round.depositLimit),
        totalDeposits: BigInt(round.totalDeposits),
        whaleDeposits: whaleDeposits,
        userDeposits: BigInt(round.userDeposits),
        remainingCapacity: BigInt(round.remainingCapacity),
        userBalance: BigInt(
          vaultBalances.find((balance) => balance.strike === round.strike)?.balance || "0",
        ),
        personalDeposits: personalDepositsMap[round.strike] || 0n,
        remainingMatchable,
        totalWhitelistDeposit,
      };
    }),
    availableStrikes,
    epochs: vaultEpochs.map((epoch) => ({
      ...epoch,
      strike: BigInt(epoch.strike),
      totalWhitelistDeposit: BigInt(epoch.totalWhitelistDeposit),
      totalMatched: BigInt(epoch.totalMatched),
    })),
    stats:
      vaultStats.length > 0
        ? {
            totalDeposits: BigInt(vaultStats[0].totalDeposits),
            totalUsers: BigInt(vaultStats[0].totalUsers),
          }
      : null,
    isLoading,
    error,
  };
}
